import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX } = FiIcons;

const TidyCalModal = ({ isOpen, onClose }) => {
  const embedRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Load TidyCal script only once
      const loadTidyCalScript = () => {
        return new Promise((resolve, reject) => {
          // Check if script is already loaded
          const existingScript = document.querySelector('script[src="https://asset-tidycal.b-cdn.net/js/embed.js"]');
          
          if (existingScript || scriptLoadedRef.current) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = 'https://asset-tidycal.b-cdn.net/js/embed.js';
          script.async = true;
          
          script.onload = () => {
            scriptLoadedRef.current = true;
            resolve();
          };
          
          script.onerror = () => {
            reject(new Error('Failed to load TidyCal script'));
          };

          document.head.appendChild(script);
        });
      };

      // Initialize TidyCal embed
      const initializeTidyCal = async () => {
        try {
          await loadTidyCalScript();
          
          // Small delay to ensure script is fully loaded
          setTimeout(() => {
            if (embedRef.current && window.TidyCal) {
              // Clear any existing content
              embedRef.current.innerHTML = '';
              
              // Reinitialize the embed
              window.TidyCal.init();
            }
          }, 100);
        } catch (error) {
          console.error('Error loading TidyCal:', error);
        }
      };

      initializeTidyCal();

      // Add escape key listener
      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not any child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black bg-opacity-60 backdrop-blur-sm"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={handleBackdropClick}
      >
        <div className="flex items-center justify-center min-h-full p-2 sm:p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300, duration: 0.3 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - More Compact */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 bg-white">
              <div>
                <h3 className="text-xl sm:text-2xl font-canela font-medium text-gray-900">
                  Schedule Your Free Discovery Call
                </h3>
                <p className="text-gray-600 font-inter text-xs sm:text-sm mt-1">
                  Let's discuss how I can support you through your journey
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:ring-offset-2"
                aria-label="Close modal"
              >
                <SafeIcon icon={FiX} className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* TidyCal Embed - Optimized for wider viewing */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 100px)' }}>
              <div className="p-2 sm:p-4">
                <div 
                  ref={embedRef}
                  className="tidycal-embed rounded-lg" 
                  data-path="dwd/discovery-call" 
                  style={{ 
                    minHeight: '700px',
                    height: '75vh',
                    background: '#fafafa', 
                    borderRadius: '8px', 
                    width: '100%' 
                  }}
                >
                  {/* Loading placeholder */}
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-500 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-inter text-sm">Loading calendar...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - More Compact */}
            <div className="border-t border-gray-100 bg-gray-50 px-4 sm:px-6 py-2 sm:py-3 text-center">
              <button
                onClick={onClose}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 font-inter transition-colors"
              >
                Close this window
              </button>
            </div>
          </motion.div>
        </div>

        {/* Mobile close indicator */}
        <div className="absolute top-4 right-4 text-white text-sm font-inter opacity-75 pointer-events-none lg:hidden">
          Tap outside to close
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TidyCalModal;